import styled from '@emotion/styled';
import { V1Service } from '@zelos/codegen/apis';
import { regex } from '@zelos/shared/constants/regex';
import { redirectTo } from '@zelos/shared/lib/redirect-util';
import { signIn } from '@zelos/shared/lib/sign.service';
import { setReactHookFormErrorByErrors, toTypedApiError } from '@zelos/shared/lib/typed-api-error';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { InputAboveDiv, InputWrapDiv, InputWrapSpan } from '../SignInput';
import SnsSign from '../SnsSign';
import ActivateUserModal from './ActivateUserModal';
import Button from '@/components/comm/Button';
import ImageButton from '@/components/comm/ImageButton';
import Input from '@/components/comm/Input';
import Spacer from '@/components/comm/Spacer';
import { breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { createErrorToast } from '@/lib/toast';
import icEyeSlash from '@/public/images/ic-eye-slash.svg';
import icEye from '@/public/images/ic-eye.svg';
import icLock from '@/public/images/ic-lock.svg';
import icMessage from '@/public/images/ic-message.svg';
type LoginFormType = { email: string, password: string };

const Container = styled.div`
position: relative;
display: grid;
width: 100%;
align-items: center;
padding: 3px 32px;
border: 1px solid ${colors.lightGrey2};
background-color: ${colors.white};
border-radius: 0 15px 15px 15px;
:after {
  position: absolute;
  top: -1px;
  left: 0;
  display: block;
  width: 50%;
  height: 1px;
  background-color: white;
  content: "";
}

@media (max-width: ${breakpointSmall}) {
  padding: 3px 20px;
}
`;

const FindPasswordSpan = styled.span`
  color: ${colors.blue};
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  line-height: 24px;
`;

const LoginButton = styled(Button)`
width: 100%;
height: 54px;
margin-top: 60px;
`;

const SignIn = () => {
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
    getValues,
  } = useForm<LoginFormType>();
  const router = useRouter();

  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSignInSubmit = async ({ email, password }: LoginFormType) => {
    try {
      await signIn(email, password);
      if (signIn.inProgress) return;
      redirectTo(router.query.destination ? decodeURIComponent(String(router.query.destination)) : '/');
    } catch (error) {
      const errorBody = toTypedApiError(error);
      if (errorBody) {
        if (errorBody.status === 423) {
          setShowActivateModal(true);
        } else {
          setReactHookFormErrorByErrors(setError, errorBody.errors);
          createErrorToast(errorBody, 'detail');
        }
      } else {
        throw error;
      }
    }
  };

  const handleActivateUser = async () => {
    try {
      const value = getValues();
      await V1Service.v1UsersActivateCreate({ requestBody: value });
      redirectTo(router.query.destination ? decodeURIComponent(String(router.query.destination)) : '/');
    } catch (error) {
      const errorBody = toTypedApiError(error);
      if (errorBody) {
        createErrorToast(errorBody, 'detail');
      } else {
        throw error;
      }
    } finally {
      setShowActivateModal(false);
    }
  };

  return (
    <>
      <Container>
        <form
          onSubmit={handleSubmit(handleSignInSubmit)}
          noValidate
        >
          <InputWrapDiv>
            <InputAboveDiv>
              <InputWrapSpan>이메일</InputWrapSpan>
            </InputAboveDiv>
            <Input
              type="email"
              placeholder="통합회원 이메일을 입력해 주세요"
              error={errors.email}
              {...register('email', {
                required: '이메일을 입력해 주세요',
                pattern: {
                  value: regex.email,
                  message: '올바르지 않은 이메일 형식입니다.',
                },
              })}
              start={<Image src={icMessage} alt="message" />}
            />
          </InputWrapDiv>
          <InputWrapDiv>
            <InputAboveDiv>
              <InputWrapSpan>비밀번호</InputWrapSpan>
              <Spacer />
              <Link href="/sign?page=resetpassword" tabIndex={-1}>
                <FindPasswordSpan>비밀번호 재설정</FindPasswordSpan>
              </Link>
            </InputAboveDiv>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="비밀번호를 입력해 주세요"
              autoComplete="on"
              error={errors.password}
              {...register('password', {
                required: '비밀번호를 입력해 주세요',
              })}
              start={<Image src={icLock} alt="lock" />}
              end={(
                <ImageButton
                  onClick={() => setShowPassword(!showPassword)}
                  src={showPassword ? icEye : icEyeSlash}
                  alt="show password"
                  width={24}
                  height={17}
                  style={{
                    objectFit: 'contain',
                  }}
                />
              )}
            />
          </InputWrapDiv>
          <LoginButton type="submit">로그인</LoginButton>
        </form>
        <SnsSign text="다른 계정으로 로그인 하기" />
      </Container>
      <ActivateUserModal
        showState={[showActivateModal, setShowActivateModal]}
        onConfirm={() => handleActivateUser()}
      />
    </>
  );
};

export default SignIn;
